import React from 'react';
import Modal from 'react-modal';
import styled from 'styled-components';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import Colors from '@components/shared/Colors';

import CloseIcon from '@images/account/icon-x.svg';
import Tick from '@images/account/sub-status-active.svg';
import Cross from '@images/account/sub-status-problem.svg';
import { CountryCode } from '@dayetopia/types';
import CompareTamponsBody from './CompareTamponsBody';

const customStyles = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    width: '540px',
    maxWidth: '90vw',
    maxHeight: '90vh',
    padding: '0',
    border: '0',
    borderRadius: '0',
    background: '#FFFBE7'
  },
  overlay: {
    background: `${Colors.navyBlue}25`
  }
};

interface RowProps {
  text: string;
  cbd: boolean;
  naked: boolean;
}

const CompareModalRow: React.FC<RowProps> = ({ text, cbd, naked }) => (
  <Row>
    <p>{text}</p>
    <SVGWrap>{naked ? <Tick /> : <Cross />}</SVGWrap>
    <SVGWrap>{cbd ? <Tick /> : <Cross />}</SVGWrap>
  </Row>
);

const Row = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #faead6;
  padding: 15px 20px;

  p {
    font-family: BrownStd;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 20px;
    letter-spacing: 0;
    text-align: left;
    width: 134px;
    color: ${Colors.darkGreen};
    flex-grow: 1;
  }
`;

const SVGWrap = styled.div`
  display: flex;
  align-items: center;
  width: 60px;
  flex-direction: column;

  svg {
    width: 20px;
  }
`;

interface Props {
  modalOpen: boolean;
  closeModal: () => void;
  country: CountryCode;
}

const SubscribeCompareModal: React.FC<Props> = ({ modalOpen, closeModal, country }) => {
  const { t } = useTranslation();
  const CBDText = country === 'US' ? 'Coated' : 'CBD';

  const items = [
    {
      text: t('components.compare-tampons-modal.highly-absorbent'),
      cbd: true,
      naked: true
    },
    {
      text: t('components.compare-tampons-modal.protective-sleeve'),
      cbd: true,
      naked: true
    },
    {
      text: t('components.compare-tampons-modal.cbd'),
      cbd: true,
      naked: false
    },
    {
      text: t('components.compare-tampons-modal.organic'),
      cbd: true,
      naked: true
    },
    {
      text: t('components.compare-tampons-modal.sanitised'),
      cbd: true,
      naked: true
    },
    {
      text: t('components.compare-tampons-modal.applicators'),
      cbd: true,
      naked: true
    },
    {
      text: t('components.compare-tampons-modal.wrappers'),
      cbd: true,
      naked: true
    },
    {
      text: t('components.compare-tampons-modal.packaging'),
      cbd: true,
      naked: true
    },
    {
      text: t('components.compare-tampons-modal.ethically'),
      cbd: true,
      naked: true
    }
  ];

  // React.useEffect(() => {
  //   modalOpen ? blockScroll() : allowScroll();
  // }, [modalOpen]);

  return (
    <Modal
      isOpen={modalOpen}
      onAfterOpen={() => null}
      onRequestClose={closeModal}
      style={customStyles}
      closeTimeoutMS={250}
      contentLabel={t('components.compare-tampons-modal.title')}
    >
      <ModalHeader>
        <h2>{t('components.compare-tampons-modal.title')}</h2>
        <button onClick={closeModal}>
          <CloseIcon />
        </button>
      </ModalHeader>
      <ModalBody>
        <CompareTamponsBody country={country} />
        <CompareHeaderRow>
          <span>{t('components.compare-tampons-modal.detailed-comparison')}</span>
          <span>{t('components.naked')}</span>
          <span>{CBDText}</span>
        </CompareHeaderRow>
        {items.map((item: any, idx: number) => (
          <CompareModalRow key={idx} text={item.text} cbd={item.cbd} naked={item.naked} />
        ))}
        <LinkText onClick={closeModal}>Close</LinkText>
      </ModalBody>
    </Modal>
  );
};

export default SubscribeCompareModal;

const ModalHeader = styled.div`
  padding: 32px;
  background: #c5e3c1;
  position: sticky;
  width: 100%;
  top: 0;
  left: 0;
  right: 0;

  h2 {
    font-family: 'BrownStd';
    font-size: 14px;
    font-style: normal;
    font-weight: 700;
    line-height: 14px;
    letter-spacing: 1px;
    text-align: center;
    text-transform: uppercase;
    color: #003b1b;

    @media (max-width: 1024px) {
      text-align: left;
    }
  }

  button {
    background: transparent;
    position: absolute;
    right: 30px;
    top: 50%;
    border: 0;
    padding: 0;
    height: 20px;
    width: 20px;
    margin-top: -10px;
    outline: none;
    cursor: pointer;

    svg {
      width: 100%;
      height: 100%;
    }

    &:hover {
      opacity: 0.5;
    }
  }
`;

const ModalBody = styled.div`
  padding: 40px 40px 30px;
  text-align: center;

  @media (max-width: 768px) {
    padding: 0 0 30px;
  }

  h3 {
    font-family: 'Domaine';
    font-style: normal;
    font-weight: normal;
    font-size: 24px;
    line-height: 140%;
    text-align: center;
    color: ${Colors.forestGreen};
    margin-bottom: 30px;
  }
`;

const LinkText = styled.span`
  font-family: 'BrownStd';
  font-size: 15px;
  font-style: normal;
  font-weight: 700;
  line-height: 24px;
  letter-spacing: 0;
  color: ${Colors.darkGreen};
  border-bottom: 2px solid ${Colors.darkGreen};
  display: inline-block;
  margin-top: 30px;
  cursor: pointer;

  &:hover {
    opacity: 0.5;
  }
`;

const CompareHeaderRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
  padding: 20px;
  border-bottom: 1px solid #faead6;

  span {
    display: flex;
    flex-direction: column;
    width: 70px;

    font-family: 'BrownStd';
    font-size: 12px;
    font-style: normal;
    font-weight: 700;
    line-height: 16px;
    letter-spacing: 1.5px;
    text-align: center;
    text-transform: uppercase;
    color: ${Colors.darkGreen};

    &:first-child {
      margin-left: 0;
      flex: 1;
      text-align: left;
      width: auto;
    }

    &:last-child {
      padding-right: 5px;
      text-align: right;
    }
  }
`;
